import type { ApiResponse } from '@repo/shared-types';
import { WORKSPACE_KEY } from './landing';

/** Thrown by `apiFetch` for any non-success response. `.message` alone is backward
 * compatible with every existing `catch (err: Error)` call site; `.status`/`.code` let a
 * caller branch on a specific failure (e.g. 409 SYNC_IN_PROGRESS) without string-matching
 * the message text. */
export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** Merges the active-workspace header into any outgoing request's headers — shared by
 * `apiFetch` and `sse.ts`'s `streamSSE` (fix round 1, MEDIUM finding: `streamSSE` used to call
 * raw `fetch()` with its own header object and never attached this, so a user switched into an
 * invited workspace got silent 404s chatting with that workspace's bots via the playground —
 * every OTHER dashboard request already carried it, only the chat stream didn't) so EVERY
 * outgoing request carries the same workspace context, not just the ones that happen to go
 * through `apiFetch`. Attaches the active workspace, written by `WorkspaceMenu.tsx`
 * (switching) and `InviteAccept.tsx` (accepting an invite). The API honors this ONLY when it
 * names a workspace the caller is a member of; absent/invalid/not-a-member all fall back
 * silently to today's behavior (first membership), so this is backward compatible with every
 * request made before a workspace was ever selected. Takes/returns a `Headers` (not a plain
 * `RequestInit`) so a caller with its own headers (`streamSSE`'s `Content-Type`) merges rather
 * than clobbers.
 */
export function withWorkspaceHeader(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  const workspaceId = localStorage.getItem(WORKSPACE_KEY);
  if (workspaceId && !headers.has('x-workspace-id')) headers.set('x-workspace-id', workspaceId);
  return headers;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = withWorkspaceHeader(init?.headers);
  const res = await fetch(path, { credentials: 'include', ...init, headers });
  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok && !contentType.includes('application/json')) {
    throw new ApiError(res.status, 'UNKNOWN', `Request failed (${res.status})`);
  }
  const body = (await res.json()) as ApiResponse<T>;
  if (!body.success) throw new ApiError(res.status, body.error.code, body.error.message);
  return body.data;
}
